// 引入 React 回调与记忆化 Hook。
import { useCallback, useMemo } from "react";
// 引入异步状态枚举。
import { PromiseState } from "@/constants";
// 引入错误码常量。
import errorCode from "@/constants/errorCode";
// 引入页面路径常量。
import { pagePathUrl } from "@/constants/url";
// 引入埋点命名空间。
import { namespace } from "@/context";
// 引入 Toast 组件。
import Toast from "@/components/Toast";
// 引入用户配置更新接口。
import { userUpdateConfigService } from "@/api/profile";
// 引入侧边栏选项类型。
import { HomeSidebarListOption } from "@/components/HomeSidebar/SideItems";
// 引入飞行距离格式化工具。
import { formatFlightDistanceNumber } from "@/utils/formatNumber";
import MyPostsIcon from "../../../assets/icon/homeSidebar/my-posts.svg";
import MyPostsActiveIcon from "../../../assets/icon/my-posts-tab-active.svg";
import NotificationsIcon from "../../../assets/icon/homeSidebar/notifications.svg";
import NotificationsActiveIcon from "../../../assets/icon/notifications-tab-active.svg";
import LikeIcon from "../../../assets/icon/like.svg";
import LikeActiveIcon from "../../../assets/icon/like-tab-active.svg";
import CollectIcon from "../../../assets/icon/collect.svg";
import CollectActiveIcon from "../../../assets/icon/collect-tab-active.svg";
import ReplyIcon from "../../../assets/icon/comment.svg";
import ReplyActiveIcon from "../../../assets/icon/comment-active.svg";

// Hook 入参类型。
interface Params {
  account: any;
  isSelfView: boolean;
  displayAccount: any;
  t: (key: string) => string;
  track?: any;
  TRACK_EVENTS?: any;
  permission: any;
  activeTab: string;
  /**
   * 保存当前 Tab 滚动位置并预设目标 Tab 位置的回调。
   * 接收当前 Tab key 和目标 Tab key，在 DOM 更新前同步执行，
   * 确保在 display 切换导致文档高度突变之前就把 scrollY 设好。
   */
  saveTabContentScrollState: (currentTabKey: string, targetTabKey?: string) => void;
  setActiveTab: (tab: string) => void;
  setShowPointsExchangeModal: (v: boolean) => void;
  setShowEditProfileModal: (v: boolean) => void;
  hideDistance: boolean;
  setHideDistance: (v: boolean) => void;
  updating: boolean;
  setUpdating: (v: boolean) => void;
}

// PC 用户中心行为逻辑 Hook（侧栏切换、埋点、操作按钮）。
export const useUserCenterPcActions = ({
  account,
  isSelfView,
  displayAccount,
  t,
  track,
  TRACK_EVENTS,
  permission,
  activeTab,
  saveTabContentScrollState,
  setActiveTab,
  setShowPointsExchangeModal,
  setShowEditProfileModal,
  hideDistance,
  setHideDistance,
  updating,
  setUpdating,
}: Params) => {
  // 切换飞行距离可见性并做乐观更新。
  const handleToggleFlightVisibility = useCallback(async () => {
    if (updating) return;
    const nextHideDistance = !hideDistance;
    const prev = hideDistance;
    const buttonName = nextHideDistance ? t("account_header_distance_private") : t("account_header_distance_public");
    const reportDistanceClick = (isSuccess: boolean, failReason: string) => {
      track?.(
        TRACK_EVENTS?.[namespace.home]?.distanceButtonsClick?.name,
        {
          button_name: buttonName,
          page_name: "MyProfilePage",
          platform_type: "pc",
          position: "Top",
          is_success: isSuccess,
          fail_reason: failReason,
        },
        namespace.home
      );
    };
    setHideDistance(nextHideDistance);
    setUpdating(true);
    try {
      await userUpdateConfigService({ hide_distance: nextHideDistance });
      reportDistanceClick(true, "");
      Toast.show({
        content: nextHideDistance ? t("account_header_flight_distance_private") : t("account_header_flight_distance_public"),
        style: { top: "26%" },
        rootClassName: "toastLineHeightUnset",
        isPc: true,
      });
    } catch (err: any) {
      setHideDistance(prev);
      reportDistanceClick(false, err?.message || t("account_header_setting_failed_retry"));
      Toast.show({
        content: err?.message || t("account_header_setting_failed_retry"),
        style: { top: "26%" },
        rootClassName: "toastLineHeightUnset",
        isPc: true,
      });
    } finally {
      setUpdating(false);
    }
  }, [TRACK_EVENTS, hideDistance, t, track, updating, setHideDistance, setUpdating]);

  // 处理“发帖”入口逻辑与埋点。
  const handlePost = useCallback(() => {
    const reportPostClick = (isSuccess: boolean, failReason: string) => {
      track?.(
        TRACK_EVENTS?.[namespace.home]?.uploadButtonsClick?.name,
        {
          button_name: t("user_center_pc_post"),
          page_name: "MyProfilePage",
          platform_type: "pc",
          position: "left",
          is_success: isSuccess,
          fail_reason: failReason,
        },
        namespace.home
      );
    };
    if (permission?.status !== PromiseState.PENDING) {
      if (permission?.code === errorCode.UNBIND_PHONE) {
        Toast.show({
          content: permission?.message || t("phone_number_is_not_bound_tip"),
          isPc: true,
        });
        reportPostClick(false, t("user_center_pc_unbound_phone"));
        setTimeout(() => {
          window.open(`${process.env.SITE_ORIGIN}${pagePathUrl.edit}`, "_blank");
        }, 1000);
        return;
      }
      const isLogin = !!account?.id;
      reportPostClick(isLogin, isLogin ? "" : t("user_center_pc_not_logged_in"));
      window.open(`${process.env.SITE_ORIGIN}${pagePathUrl.edit}`, "_blank");
      return;
    }
    reportPostClick(false, t("user_center_pc_permission_not_ready"));
  }, [TRACK_EVENTS, account?.id, permission?.code, permission?.message, permission?.status, t, track]);

  // 侧栏 tab 切换埋点。
  const reportSidebarTabSwitch = useCallback(
    (tabKey: string, buttonName: string) => {
      if (activeTab === tabKey) return;
      track?.(
        TRACK_EVENTS?.[namespace.home]?.accountBarButtonClick?.name,
        {
          button_name: buttonName,
          page_name: "MyProfilePage",
          platform_type: "pc",
          position: "Bottom",
        },
        namespace.home
      );
    },
    [TRACK_EVENTS, activeTab, track]
  );

  // 处理积分兑换点击。
  const handlePointsExchangeClick = useCallback(() => {
    track?.(
      TRACK_EVENTS?.[namespace.home]?.pointsButtonClick?.name,
      {
        button_name: t("user_profile_action_exchange"),
        page_name: "MyProfilePage",
        platform_type: "pc",
        position: "Bottom",
      },
      namespace.home
    );
    setShowPointsExchangeModal(true);
  }, [TRACK_EVENTS, setShowPointsExchangeModal, t, track]);

  // 处理设备图标点击与跳转埋点。
  const handleDeviceIconClick = useCallback((deviceLink?: string, productName?: string) => {
    if (!deviceLink) return;
    track?.(
      TRACK_EVENTS?.[namespace.home]?.productEntryClick?.name,
      {
        page_name: isSelfView ? "MyProfilePage" : "UserProfilePage",
        platform_type: "pc",
        product_name: productName,
        account_slug: displayAccount?.id || account?.id,
      },
      namespace.home
    );
    window.open(deviceLink, "_blank");
  }, [TRACK_EVENTS, account?.id, displayAccount?.id, isSelfView, track]);

  // 处理编辑资料按钮点击。
  const handleEditProfileClick = useCallback(() => {
    track?.(
      TRACK_EVENTS?.[namespace.home]?.editprofileButtonClick?.name,
      {
        page_name: "MyProfilePage",
        platform_type: "pc",
        button_name: t("user_profile_edit_profile"),
      },
      namespace.home
    );
    setShowEditProfileModal(true);
  }, [TRACK_EVENTS, setShowEditProfileModal, t, track]);

  /**
   * 统一 Tab 切换入口。
   * 1. 上报侧栏 Tab 切换埋点
   * 2. 保存当前 Tab 滚动位置，并根据当前/目标的吸顶状态预设目标 Tab 的 scrollY
   *    （在 setActiveTab 导致 DOM 更新之前同步完成，避免 display 切换后 scrollY 被浏览器 clamp）
   * 3. 更新 activeTab，触发 React 重渲染（display:none/block 切换）
   */
  const handleTabSwitch = useCallback((newTabKey: string, buttonName: string) => {
    if (activeTab === newTabKey) return;
    reportSidebarTabSwitch(newTabKey, buttonName);
    saveTabContentScrollState(activeTab, newTabKey);
    setActiveTab(newTabKey);
  }, [activeTab, reportSidebarTabSwitch, saveTabContentScrollState, setActiveTab]);

  // 组装侧边栏选项（本人视角额外包含点赞/收藏/消息）。
  const sidebarOptions = useMemo(() => {
    const options: HomeSidebarListOption[] = [
      {
        key: "myPosts",
        label: isSelfView ? t("user_center_pc_my_posts") : t("user_center_pc_ta_posts"),
        path: "myPosts",
        icon: { normal: <MyPostsIcon />, active: <MyPostsActiveIcon /> },
        onClick: () => {
          handleTabSwitch("myPosts", isSelfView ? t("user_center_pc_my_posts") : t("user_center_pc_ta_posts"));
        },
      },
    ];
    if (isSelfView) {
      options.push(
        {
          key: "myReplies",
          label: t("user_center_pc_my_comments"),
          path: "myReplies",
          icon: {
            normal: <ReplyIcon width={28} height={28} />,
            active: <ReplyActiveIcon width={28} height={28} />,
          },
          onClick: () => {
            handleTabSwitch("myReplies", t("user_center_pc_my_comments"));
          },
        },
        {
          key: "myLikes",
          label: t("user_center_pc_my_likes"),
          path: "myLikes",
          icon: { normal: <LikeIcon width={28} height={28} />, active: <LikeActiveIcon /> },
          onClick: () => {
            handleTabSwitch("myLikes", t("user_center_pc_my_likes"));
          },
        },
        {
          key: "myCollections",
          label: t("user_center_pc_my_collections"),
          path: "myCollections",
          icon: { normal: <CollectIcon width={28} height={28} />, active: <CollectActiveIcon /> },
          onClick: () => {
            handleTabSwitch("myCollections", t("user_center_pc_my_collections"));
          },
        },
        {
          key: "messages",
          label: t("user_center_pc_messages"),
          path: "messages",
          icon: { normal: <NotificationsIcon />, active: <NotificationsActiveIcon /> },
          onClick: () => {
            handleTabSwitch("messages", t("user_center_pc_messages"));
          },
          badge: account?.messages,
        }
      );
    }
    return options;
  }, [account?.messages, handleTabSwitch, isSelfView, t]);

  // 展示层设备数据归一化。
  const devices = Array.isArray(displayAccount?.devices) ? displayAccount.devices : [];
  // 设备数量归一化。
  const deviceCount = typeof displayAccount?.deviceCount === "number" ? displayAccount.deviceCount : devices.length;
  // 飞行距离格式化值。
  const displayFlightDistance = formatFlightDistanceNumber(Number(displayAccount?.flightDistance || 0));
  // 管理员视图数据。
  const adminViewData = displayAccount?.admin_view;
  const adminUid = adminViewData?.uid ?? displayAccount?.id;
  const adminEmail = adminViewData?.email ?? displayAccount?.email;
  const adminIp = adminViewData?.last_login_ip ?? displayAccount?.ip;
  const adminLocation = adminViewData?.last_login_location ?? displayAccount?.location;
  const adminLastLoginTime = adminViewData?.last_login_time ?? displayAccount?.lastLoginTime;
  // 设备预览列表（最多 4 个）。
  const previewDevices = devices.slice(0, 4);
  // 是否显示“更多设备”入口。
  const showMoreDevices = deviceCount > 4;
  // 是否展示飞行距离。
  const showFlightInfo = displayAccount?.flightDistance > 0 && (isSelfView || !hideDistance);

  // 返回页面层消费的行为方法与视图模型。
  return {
    handleToggleFlightVisibility,
    handlePost,
    handlePointsExchangeClick,
    handleDeviceIconClick,
    handleEditProfileClick,
    sidebarOptions,
    deviceCount,
    displayFlightDistance,
    adminUid,
    adminEmail,
    adminIp,
    adminLocation,
    adminLastLoginTime,
    previewDevices,
    showMoreDevices,
    showFlightInfo,
  };
};
